import React from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { FilterOptions } from "@/hooks/useProductFiltering";

interface ProductFilterControlsProps {
  categories: string[];
  filters: FilterOptions;
  onFiltersChange: (filters: Partial<FilterOptions>) => void;
  showAdminControls?: boolean;
}

export const ProductFilterControls: React.FC<ProductFilterControlsProps> = ({
  categories,
  filters,
  onFiltersChange,
  showAdminControls = false,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-grow">
          <Input
            placeholder="Search products..."
            aria-label="Search products"
            value={filters.searchTerm}
            onChange={(e) => onFiltersChange({ searchTerm: e.target.value })}
            className="w-full bg-muted/30 border-muted/50 focus:border-hivetivity-primary focus:ring-1 focus:ring-hivetivity-primary/30 transition-all"
          />
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <Select
            value={filters.selectedCategory}
            onValueChange={(value) => onFiltersChange({ selectedCategory: value })}
          >
            <SelectTrigger className="w-[180px] bg-muted/30 border-muted/50 focus:ring-1 focus:ring-hivetivity-primary/30" aria-label="Select category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select 
            value={filters.sortBy} 
            onValueChange={(value) => onFiltersChange({ sortBy: value })}
          >
            <SelectTrigger className="w-[180px] bg-muted/30 border-muted/50 focus:ring-1 focus:ring-hivetivity-primary/30" aria-label="Sort products">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name (A-Z)</SelectItem>
              <SelectItem value="price-asc">Price (Low to High)</SelectItem>
              <SelectItem value="price-desc">Price (High to Low)</SelectItem>
              <SelectItem value="newest">Newest First</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {showAdminControls && (
        <>
          <Tabs value={filters.statusTab} onValueChange={(value) => onFiltersChange({ statusTab: value })} className="w-full">
            <TabsList className="w-full max-w-md mx-auto grid grid-cols-4 bg-muted/30">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="inactive">Inactive</TabsTrigger>
              <TabsTrigger value="archived">Archived</TabsTrigger>
            </TabsList>
          </Tabs>
          <Separator variant="subtle" className="mt-2" />
        </>
      )}
    </div>
  );
};
